/**
 * Step 2 — SKU code tables (Phase 65 / BLU-594 PR4). Embeds the full mapping
 * editor inline (CodeTablesEditorInline owns its own settings buffer + a local
 * save/discard footer) so the operator maps product types / colours / sizes
 * without leaving the wizard. A link to the full Product sync surface remains
 * for the wider context (coverage, per-product preview).
 *
 * In the client flow this step is hidden by the step model when the client
 * uses the 3PL's garments, so it never mounts and the editor's blocking
 * catalogue scan never fires for a client that does not need code tables.
 *
 * BLU-1442: in the owner (3PL) flow, someone who can open Base garments also
 * gets a link to its Codes panel on the Colours tab, where an empty colour
 * table offers the starter colours. A link only: nothing here writes, so the
 * editor's unsaved changes are never at risk. Never in the client flow.
 */
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { ExternalLink, Tags } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useBaseGarmentsAccess } from '@/features/base-garments/hooks/useBaseGarmentsAccess';
import { baseGarmentsCodesHref } from '@/features/base-garments/lib/base-garments-url';
import { CodeTablesEditorInline } from '@/features/product-sync/components/CodeTablesEditorInline';
import { StepPane } from '../shell/StepPane';

interface Step2Props {
  counts: { productTypes: number; colours: number; sizes: number };
  /** The client flow: no Base garments link (a client uses the 3PL's garments). */
  clientFlow: boolean;
}

export function Step2CodeTables({ counts, clientFlow }: Step2Props) {
  const { t } = useTranslation('onboarding');
  const navigate = useNavigate();
  const canOpenBaseGarments = useBaseGarmentsAccess();
  const showCodesLink = !clientFlow && canOpenBaseGarments;

  const openButton = (
    <Button type="button" variant="outline" size="sm" className="h-8 gap-1.5 px-3 text-xs" onClick={() => navigate('/product-sync?tab=code')}>
      <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
      {t('step2.open')}
    </Button>
  );

  const codesLink = showCodesLink ? (
    <Button asChild size="sm" className="h-8 gap-1.5 bg-threadheads-purple px-3 text-xs text-white hover:bg-threadheads-purple/90">
      <Link to={baseGarmentsCodesHref('colours')}>
        <Tags className="h-3.5 w-3.5" aria-hidden="true" />
        {t('step2.baseGarmentsLink')}
      </Link>
    </Button>
  ) : null;

  return (
    <StepPane
      title={t('step2.title')}
      description={t('step2.description')}
      primary={
        codesLink ? (
          <div className="flex flex-wrap justify-end gap-2">
            {openButton}
            {codesLink}
          </div>
        ) : (
          openButton
        )
      }
      summary={t('step2.summary', {
        productTypes: counts.productTypes,
        colours: counts.colours,
        sizes: counts.sizes,
      })}
    >
      <CodeTablesEditorInline />
    </StepPane>
  );
}
